export type Permission = {
  id: number;
  name: string;
  label: string;
  group: string;
  type: string;
  required: string[];
};

export type PermissionsFilters = Record<string, string>;

export enum PermissionsEntities {
  VOTING = "voting",
  MEETING = "meeting",
  TASK = "task",
  WORKSPACE = "workspace",
}

export enum PermissionTypes {
  GLOBAL = "global",
  MEETING = "meeting",
  VOTING = "voting",
}

export enum UserPermissions {
  ACCESS_CALENDAR = "access_calendar",
  ACCESS_DOCUMENTS = "access_documents",
  ACCESS_MEETINGS = "access_meetings",
  ACCESS_SETTINGS = "access_settings",
  ACCESS_SETTINGS_COMPANY = "access_settings_company",
  ACCESS_SETTINGS_GENERAL = "access_settings_general",
  ACCESS_SETTINGS_PRIVACY = "access_settings_privacy",
  ACCESS_SETTINGS_ROLES = "access_settings_roles",
  ACCESS_SETTINGS_STATISTICS = "access_settings_statistics",
  ACCESS_SETTINGS_USERS = "access_settings_users",
  ACCESS_SETTINGS_WORKSPACES = "access_settings_workspaces",
  ACCESS_TASKS = "access_tasks",
  ACCESS_VOTINGS = "access_votings",
  ACCESS_WORKSPACES = "access_workspaces",
  ACTIVATE_USER = "activate_user",
  APPROVE_ANY_MEETING = "approve_any_meeting",
  ASSIGN_ANY_USER_TO_TASK = "assign_any_user_to_task",
  ASSIGN_ROLE = "assign_role",
  ASSIGN_USER_TO_ANY_TASK = "assign_user_to_any_task",
  ASSIGN_WORKSPACE_USER_TO_TASK = "assign_workspace_user_to_task",
  CAN_BE_ASSIGNED_TO_APPROVE_MEETING = "can_be_assigned_to_approve_meeting",
  CHANGE_ANY_TASK_STATUS = "change_any_task_status",
  CREATE_MEETING_FOR_ANY_WORKSPACE = "create_meeting_for_any_workspace",
  CREATE_MEETING_FOR_JOINED_WORKSPACE = "create_meeting_for_joined_workspace",
  CREATE_MEETING_ROLE = "create_meeting_role",
  CREATE_ROLE = "create_role",
  CREATE_TASK = "create_task",
  CREATE_USER = "create_user",
  CREATE_VOTING = "create_voting",
  CREATE_VOTING_ROLE = "create_voting_role",
  CREATE_WORKSPACE = "create_workspace",
  DEACTIVATE_USER = "deactivate_user",
  DELETE_ANY_DOCUMENT = "delete_any_document",
  DELETE_ANY_MEETING = "delete_any_meeting",
  DELETE_ANY_TASK = "delete_any_task",
  DELETE_ANY_WORKSPACE = "delete_any_workspace",
  DELETE_MEETING_ROLE = "delete_meeting_role",
  DELETE_ROLE = "delete_role",
  DELETE_VOTING_ROLE = "delete_voting_role",
  EDIT_ANY_MEETING = "edit_any_meeting",
  EDIT_ANY_TASK = "edit_any_task",
  EDIT_ANY_VOTING = "edit_any_voting",
  EDIT_ANY_WORKSPACE = "edit_any_workspace",
  EDIT_ASSIGNED_TASK = "edit_assigned_task",
  EDIT_MEETING_MINUTES_FROM_ANY_MEETING = "edit_meeting_minutes_from_any_meeting",
  EDIT_MEETING_ROLE = "edit_meeting_role",
  EDIT_ROLE = "edit_role",
  EDIT_USER = "edit_user",
  EDIT_USER_QUORUM_NUMBER = "edit_user_quorum_number",
  EDIT_USER_SHARES = "edit_user_shares",
  EDIT_VOTING_ROLE = "edit_voting_role",
  END_ANY_VOTING = "end_any_voting",
  INVITE_ANY_USER_TO_MEETING = "invite_any_user_to_meeting",
  INVITE_ANY_USER_TO_VOTING = "invite_any_user_to_voting",
  INVITE_EXTERNAL_USERS = "invite_external_users",
  INVITE_USER_TO_ANY_WORKSPACE = "invite_user_to_any_workspace",
  INVITE_USER_TO_JOINED_WORKSPACE = "invite_user_to_joined_workspace",
  INVITE_WORKSPACE_USER_TO_MEETING = "invite_workspace_user_to_meeting",
  INVITE_WORKSPACE_USER_TO_VOTING = "invite_workspace_user_to_voting",
  LOCK_ANY_VOTING = "lock_any_voting",
  MAKE_PRIVATE_MEETING = "make_private_meeting",
  REMOVE_USER_FROM_ANY_WORKSPACE = "remove_user_from_any_workspace",
  REQUEST_FOR_INVITATION_TO_MEETING = "request_for_invitation_to_meeting",
  SEND_ANY_MEETING_TO_APPROVAL = "send_any_meeting_to_approval",
  START_ANY_VOTING = "start_any_voting",
  VIEW_ANY_MEETING = "view_any_meeting",
  VIEW_ANY_MEETING_ALL_SECTIONS_AND_SUBSECTIONS = "view_any_meeting_all_sections_and_subsections",
  VIEW_ANY_MEETING_DETAILS = "view_any_meeting_details",
  VIEW_ANY_MEETING_SUMMARY = "view_any_meeting_summary",
  VIEW_ANY_TASK = "view_any_task",
  VIEW_ANY_TASK_DETAILS = "view_any_task_details",
  VIEW_ANY_USER = "view_any_user",
  VIEW_ANY_USER_DETAILS = "view_any_user_details",
  VIEW_ANY_VOTING = "view_any_voting",
  VIEW_ANY_VOTING_DETAILS = "view_any_voting_details",
  VIEW_ANY_WORKSPACE = "view_any_workspace",
  VIEW_ANY_WORKSPACE_DETAILS = "view_any_workspace_details",
  VIEW_PRIVATE_MEETING = "view_private_meeting",
  VIEW_WORKSPACE_MEETING = "view_workspace_meeting",
  VIEW_WORKSPACE_TASK = "view_workspace_task",
}

export enum UserVotingPermissions {
  APPROVE_SUMMARY = "approve_summary",
  ASSIGN_ROLES = "assign_roles",
  ATTEND = "attend",
  DELETE = "delete",
  EDIT = "edit",
  EDIT_NOTE = "edit_note",
  END = "end",
  GET_SUMMARY = "get_summary",
  LOCK = "lock",
  REQUEST_SUMMARY_APPROVAL = "request_summary_approval",
  START = "start",
  TRANSFER_VOTE = "transfer_vote",
  UPDATE = "update",
  UPDATE_CONCLUSION = "update_conclusion",
  VIEW = "view",
  VIEW_RESULTS = "view_results",
  VIEW_RESULTS_WITH_DETAILS = "view_results_with_details",
  VOTE = "vote",
  DOWNLOAD_SIGNED_SUMMARY = "download_signed_summary",
  INVITE_USER = "invite_user",
  REMOVE_USER = "remove_user",
}

export enum UserTaskPermissions {
  CHANGE_STATUS = "change_status",
  DELETE = "delete",
  EDIT = "edit",
  REJECT = "reject",
  UPDATE = "update",
  VIEW = "view",
  WORK = "work",
}

export enum UserWorkspacePermissions {
  ADD_USERS = "add_users",
  ARCHIVE = "archive",
  DELETE = "delete",
  EDIT = "edit",
  REMOVE_USERS = "remove_users",
  UPDATE = "update",
  VIEW = "view",
}

export enum UserMeetingPermissions {
  APPROVE = "approve",
  APPROVE_MINUTES = "approve_minutes",
  ASSING_ROLES = "assign_roles",
  ATTEND = "attend",
  BEGIN = "begin",
  CHANGE_SECTION = "change_section",
  CREATE_NOTE = "create_note",
  CREATE_TASK = "create_task",
  CREATE_VOTING = "create_voting",
  DELETE = "delete",
  EDIT = "edit",
  END = "end",
  GENERATE_PDF_SUMMARY = "generate_pdf_summary",
  MANAGE = "manage",
  POSTPONE = "postpone",
  SEND_MINUTES_TO_APPROVAL = "send_minutes_to_approval",
  SEND_TO_APPROVAL = "send_to_approval",
  START_REVIEW = "start_review",
  UPDATE = "update",
  UPLOAD_SIGNED_SUMMARY = "upload_signed_summary",
  VIEW = "view",
  VIEW_SUMMARY = "view_summary",
  INVITE_USER = "invite_user",
  REMOVE_USER = "remove_user",
  ASSIGN_USER_ROLE = "assign_user_role",
}
